'use client';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@gartner-digital-markets/nimbus-v3-ui';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useEmailStorage } from '../hooks/useEmailStorage';

interface EmailFieldProps {
  name?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  /** When true, the inline validation message is not rendered as part of
   *  this component — callers can render it separately to keep error
   *  text out of flex/grid alignment between the input and a sibling button. */
  hideMessage?: boolean;
  onPointerDown?: () => void;
  onInput?: () => void;
}

export const EmailField = ({
  name = 'emailAddress',
  label = 'Email Address',
  placeholder = '',
  required = true,
  hideMessage = false,
  onPointerDown,
  onInput,
}: EmailFieldProps) => {
  const { control, getFieldState, setValue, watch } = useFormContext();
  const { getStoredEmail } = useEmailStorage();
  const currentValue = watch(name);

  const hasPrefilled = useRef(false);
  // We use a ref to ensure we only attempt to prefill once
  useEffect(() => {
    if (hasPrefilled.current || currentValue) return;

    const storedEmail = getStoredEmail();
    if (storedEmail) {
      setValue(name, storedEmail, { shouldValidate: true });
      hasPrefilled.current = true;
    }
  }, [getStoredEmail, name, setValue, currentValue]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const fieldState = getFieldState(name);

        return (
          <FormItem>
            <FormLabel className="mb-1 block flex items-center gap-1 text-base leading-6 font-bold">
              {label}
              {required && (
                <span aria-hidden={true} className="text-nimbus-destructive">
                  *
                </span>
              )}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                type="email"
                placeholder={placeholder}
                error={!!fieldState.error}
                onPointerDown={() => onPointerDown?.()}
                onInput={(event) => {
                  field.onChange(event);
                  onInput?.();
                }}
                data-testid="bx-forms-email-input"
              />
            </FormControl>
            {hideMessage ? null : <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
};
